import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { DevicesApi, DevicesQueryKey } from '@frontend/api-devices';
import { E911Api, E911QueryKeys } from '@frontend/api-e911-addresses';
import { Trans, useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useQuery, useMutation } from '@frontend/react-query-helpers';
import {
  CheckboxField,
  DropdownField,
  Modal,
  ModalControlModalProps,
  Text,
  Truncated,
  Table,
  useAlert,
  useFormField,
  useModalControl,
} from '@frontend/design-system';
import { queryKeys as phoneQueryKeys } from '../query-keys';
import { trackingId } from '../tracking';
import { formatE911AddressForDisplay } from '../utils/e911-address';
import { logError } from '../utils/loggers';
import { E911AddressModal } from './devices/e911/e911-address-modal';
import { getE911CreateSuccessMessage, useCreateE911Address } from './devices/e911/use-e911-address-form';

type Props = ModalControlModalProps & {
  deviceName?: string;
  deviceId?: string;
  deviceMacAddress?: string;
  assignedAddressId?: string;
  unassignedActiveDevices?: UnassignedE911Device[];
};

type UnassignedE911Device = NonNullable<
  Awaited<ReturnType<typeof DevicesApi.ListUnassignedE911Devices>>['unassignedDevices']
>[number];

type AssignableDevice = UnassignedE911Device;

export const AssignE911AddressModal = ({
  deviceName,
  deviceId,
  deviceMacAddress,
  assignedAddressId,
  unassignedActiveDevices = [],
  ...modalProps
}: Props) => {
  const { t } = useTranslation('phone', { keyPrefix: 'e911' });
  const alerts = useAlert();
  const queryClient = useQueryClient();
  const multipleDevicesModal = useModalControl();
  const addAddressModalControl = useModalControl();
  const successModalControl = useModalControl();
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [remainingUnassignedDevices, setRemainingUnassignedDevices] = useState<UnassignedE911Device[]>([]);
  const [initialUnassignedCount, setInitialUnassignedCount] = useState(0);
  // prevent re-initialization if unassignedActiveDevices reference changes while modal is open
  const sessionInitializedRef = useRef(false);

  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const isMultiLocation = settingsTenantLocation?.locationType === 'unify';

  const { data: { addresses } = { addresses: [] } } = useQuery({
    queryKey: [tenantId, ...E911QueryKeys.queryKeys.listAddresses()],
    queryFn: () => E911Api.list({ tenantId }),
    enabled: !!tenantId && modalProps.show,
    select: (data) => ({
      ...data,
      addresses: data.addresses
        ?.slice()
        .sort((firstAddress, secondAddress) => firstAddress.name.localeCompare(secondAddress.name)),
    }),
    meta: {
      error: {
        message: 'AssignE911AddressModal:useQuery | Failed to fetch E911 addresses',
        data: { tenantId },
      },
    },
  });

  // This initializes per-open modal session state once and resets it on close,
  // so bulk assignment progress is stable even if the source prop re-renders.
  useEffect(() => {
    if (!modalProps.show) {
      sessionInitializedRef.current = false;
      setRemainingUnassignedDevices([]);
      setInitialUnassignedCount(0);
      return;
    }
    if (sessionInitializedRef.current || unassignedActiveDevices.length === 0) {
      return;
    }
    sessionInitializedRef.current = true;
    setRemainingUnassignedDevices(unassignedActiveDevices);
    setInitialUnassignedCount(unassignedActiveDevices.length);
  }, [modalProps.show, unassignedActiveDevices]);

  const devices: AssignableDevice[] = remainingUnassignedDevices;

  const addressFieldProps = useFormField({ type: 'dropdown', value: assignedAddressId ?? '' }, [assignedAddressId]);
  const confirmFieldProps = useFormField({ type: 'checkbox', value: false });
  const isAddingNewAddress = addressFieldProps.value === 'addNewAddress';

  // Selecting "Add new E911 address" should open the create-address modal exactly when
  // that option becomes active, but not reopen if the modal is already displayed.
  useEffect(() => {
    if (!isAddingNewAddress || addAddressModalControl.modalProps.show) {
      return;
    }

    addAddressModalControl.openModal();
  }, [isAddingNewAddress, addAddressModalControl]);

  const selectedAddress = addresses?.find((addr) => addr.id === addressFieldProps.value);
  const fullAddress = selectedAddress ? formatE911AddressForDisplay(selectedAddress) : '';

  const effectiveRemainingDevices =
    remainingUnassignedDevices.length === 0 && modalProps.show ? unassignedActiveDevices : remainingUnassignedDevices;
  const isBannerBulkAssignmentFlow = !deviceId && effectiveRemainingDevices.length > 0;
  const effectiveInitialCount = initialUnassignedCount > 0 ? initialUnassignedCount : effectiveRemainingDevices.length;

  const activeDeviceForAssignment = deviceId
    ? effectiveRemainingDevices.find((device) => device.deviceId === deviceId) ??
      unassignedActiveDevices.find((device) => device.deviceId === deviceId)
    : effectiveRemainingDevices[0];
  const activeDeviceId = deviceId ?? activeDeviceForAssignment?.deviceId ?? '';

  const activeDeviceMac = activeDeviceForAssignment?.macAddress
    ? activeDeviceForAssignment.macAddress.toUpperCase()
    : '';
  const activeDeviceName = activeDeviceForAssignment?.name || deviceName || t('this device');
  const assignedDeviceIndex =
    effectiveInitialCount > 0 && effectiveRemainingDevices.length > 0
      ? effectiveInitialCount - effectiveRemainingDevices.length + 1
      : 1;
  const hasSubtitleDeviceContext = (!!activeDeviceForAssignment && effectiveInitialCount > 0) || !!deviceId;

  const subtitleDeviceText = hasSubtitleDeviceContext
    ? activeDeviceForAssignment
      ? t('{{deviceName}}, {{macAddress}} ({{current}}/{{total}} active devices)', {
          deviceName: activeDeviceName,
          macAddress: activeDeviceMac,
          current: assignedDeviceIndex,
          total: effectiveInitialCount,
        })
      : deviceMacAddress
      ? `${activeDeviceName}, ${deviceMacAddress.toUpperCase()}`
      : activeDeviceName
    : null;

  const { mutateAsync: assignAddress, isLoading } = useMutation({
    mutationFn: ({ addressId, targetDeviceId }: { addressId: string; targetDeviceId: string }) =>
      DevicesApi.UpdateDeviceE911Address({
        deviceId: targetDeviceId,
        e911Address: {
          id: addressId,
          name: '',
        },
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries([tenantId, ...DevicesQueryKey.queryKeys.devicesList()]);
      queryClient.invalidateQueries([variables.targetDeviceId, ...DevicesQueryKey.queryKeys.deviceAddressById()]);
      queryClient.invalidateQueries([tenantId, ...E911QueryKeys.queryKeys.listAddresses()]);
      queryClient.invalidateQueries(phoneQueryKeys.settings.listUnassignedE911Devices(tenantId));

      if (!isBannerBulkAssignmentFlow) {
        alerts.success(t('E911 address successfully assigned to {{deviceName}}.', { deviceName: activeDeviceName }));
        modalProps.onClose();
        return;
      }

      const updatedUnassignedDevices = remainingUnassignedDevices.filter(
        (device) => device.deviceId !== variables.targetDeviceId
      );
      setRemainingUnassignedDevices(updatedUnassignedDevices);

      if (updatedUnassignedDevices.length === 0) {
        modalProps.onClose();
        successModalControl.openModal();
        return;
      }

      // Require a fresh address selection and location confirmation for each device in the wizard.
      addressFieldProps.onChange({ name: 'e911-address', value: '' });
      confirmFieldProps.onChange({ name: 'e911-confirm', value: false });
      alerts.success(t('E911 address successfully assigned to {{deviceName}}.', { deviceName: activeDeviceName }));
    },
    onError: () => {
      alerts.error(t('Failed to assign E911 address. Please try again or contact Weave for help.'));
      logError('AssignE911AddressModal:assignAddressMutation | Failed to assign E911 address', {
        data: { tenantId, deviceId: activeDeviceId },
      });
    },
  });

  const { mutateAsync: assignAddressToMultipleDevices, isLoading: isAssigningMultiple } = useMutation({
    mutationFn: (addressId: string) =>
      DevicesApi.BatchUpdateDevices({
        tenantId,
        deviceIds: selectedDeviceIds,
        e911AddressId: addressId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([tenantId, ...DevicesQueryKey.queryKeys.devicesList()]);
      queryClient.invalidateQueries([tenantId, ...E911QueryKeys.queryKeys.listAddresses()]);
      queryClient.invalidateQueries(phoneQueryKeys.settings.listUnassignedE911Devices(tenantId));

      const updatedRemaining = remainingUnassignedDevices.filter(
        (device) => !selectedDeviceIds.includes(device.deviceId)
      );

      multipleDevicesModal.closeModal();
      setSelectedDeviceIds([]);

      if (updatedRemaining.length === 0) {
        setRemainingUnassignedDevices([]);
        modalProps.onClose();
        successModalControl.openModal();
        return;
      }

      setRemainingUnassignedDevices(updatedRemaining);
      setInitialUnassignedCount(updatedRemaining.length);
      alerts.success(
        t('E911 address successfully assigned to {{count}} devices.', { count: selectedDeviceIds.length })
      );
    },
    onError: () => {
      alerts.error(t('Failed to assign E911 address. Please try again or contact Weave for help.'));
      logError('AssignE911AddressModal:assignAddressToMultipleDevicesMutation | Failed to assign E911 address', {
        data: { tenantId, selectedDeviceIds },
      });
    },
  });

  const { createAddress, isCreatingAddress, addressSaveError, clearAddressSaveError } = useCreateE911Address({
    tenantId,
    onSuccess: (createdAddress, submittedAddress) => {
      if (addresses?.some(({ id }) => id === createdAddress.id)) {
        alerts.warning(
          t('Duplicate address. {{addressName}} ({{address}})', {
            addressName: createdAddress.name || t('Address'),
            address: formatE911AddressForDisplay(createdAddress),
          })
        );
      } else {
        alerts.success(getE911CreateSuccessMessage(t, submittedAddress, createdAddress));
      }
      queryClient.invalidateQueries([tenantId, ...E911QueryKeys.queryKeys.listAddresses()]);
      clearAddressSaveError();
      addressFieldProps.onChange({ name: 'e911-address', value: createdAddress.id });
      addAddressModalControl.closeModal();
    },
  });

  const canAssign = !!addressFieldProps.value && !isAddingNewAddress && confirmFieldProps.value && !!activeDeviceId;
  const canShowAssignToMultiple = effectiveInitialCount >= 2;
  const primaryAssignLabel = canShowAssignToMultiple ? t('Assign and Continue') : t('Assign to This Device');

  const handleAssignToMultipleDevices = () => {
    setSelectedDeviceIds([]);
    multipleDevicesModal.openModal();
  };

  const handleCloseAddAddressModal = () => {
    clearAddressSaveError();
    addAddressModalControl.closeModal();

    if (addressFieldProps.value === 'addNewAddress') {
      addressFieldProps.onChange({ name: 'e911-address', value: '' });
    }
  };

  return (
    <>
      <Modal {...modalProps} minWidth={600}>
        <Modal.Header onClose={modalProps.onClose}>{t('Assign E911 Address')}</Modal.Header>
        {subtitleDeviceText && <Modal.Label>{subtitleDeviceText}</Modal.Label>}
        <Modal.Body>
          <Text>
            <Trans t={t} values={{ deviceName: activeDeviceName }}>
              Select an E911 address for <strong>{{ deviceName }}</strong>. This address is used to send the call to the
              local public safety answering point and is sent to emergency operators when someone dials 911 from this
              device.
            </Trans>
          </Text>
          <Text css={{ marginBottom: 'var(--fabric-space-gap-md)' }}>
            <Trans t={t}>
              After assigning an address, dial <strong>933</strong> to confirm the registered emergency address. This is
              a test call and will not contact emergency services.
            </Trans>
          </Text>
          <DropdownField {...addressFieldProps} name='e911-address' label={t('Select E911 Address')}>
            <DropdownField.Option
              value='addNewAddress'
              data-trackingid={trackingId({ context: 'setting', feature: 'e911', details: 'add-new-address' })}
            >
              <Text weight='semibold' css={{ color: 'var(--fabric-color-icon-primary)' }}>
                {t('Add new E911 address')}
              </Text>
            </DropdownField.Option>
            {addresses?.map((address) => {
              const fullAddress = formatE911AddressForDisplay(address);
              return (
                <DropdownField.Option key={address.id} value={address.id} searchValue={address.name}>
                  <Truncated as='div' tooltipOptions={{ placement: 'right' }}>
                    {address.name} ({fullAddress})
                  </Truncated>
                </DropdownField.Option>
              );
            })}
          </DropdownField>
          <CheckboxField
            {...confirmFieldProps}
            name='e911-confirm'
            label={t('I confirm that this device is located at the preceding address.')}
            disabled={!addressFieldProps.value || isAddingNewAddress}
          />
        </Modal.Body>
        <Modal.Footer
          primary={{
            label: primaryAssignLabel,
            onClick: async () => {
              await assignAddress({
                addressId: addressFieldProps.value,
                targetDeviceId: activeDeviceId,
              });
            },
            disabled: !canAssign || isLoading,
            loading: isLoading,
            trackingId: trackingId({
              context: 'setting',
              feature: 'e911',
              details: 'assign-e911-address-single-device',
            }),
          }}
          secondary={{
            ...(canShowAssignToMultiple
              ? {
                  label: t('Assign to Multiple Devices'),
                  onClick: handleAssignToMultipleDevices,
                  disabled: !addressFieldProps.value || isLoading,
                  trackingId: trackingId({
                    context: 'setting',
                    feature: 'e911',
                    details: 'assign-e911-addresses-multiple',
                  }),
                }
              : {}),
          }}
        />
      </Modal>
      <AssignMultipleDevicesModal
        addressId={addressFieldProps.value}
        addressName={selectedAddress?.name ?? ''}
        fullAddress={fullAddress}
        devices={devices}
        tenantId={tenantId}
        isMultiLocation={isMultiLocation}
        selectedDeviceIds={selectedDeviceIds}
        onSelectedDevicesChange={setSelectedDeviceIds}
        onAssign={async () => {
          await assignAddressToMultipleDevices(addressFieldProps.value);
        }}
        isLoading={isAssigningMultiple}
        {...multipleDevicesModal.modalProps}
      />
      <E911AddressModal
        {...addAddressModalControl.modalProps}
        onSave={createAddress}
        onCancel={handleCloseAddAddressModal}
        onBack={handleCloseAddAddressModal}
        isLoading={isCreatingAddress}
        addressSaveError={addressSaveError}
        clearAddressSaveError={clearAddressSaveError}
        addresses={addresses}
        onUseExistingAddress={(address) => {
          addressFieldProps.onChange({ name: 'e911-address', value: address.id });
          clearAddressSaveError();
          addAddressModalControl.closeModal();
        }}
      />
      <Modal {...successModalControl.modalProps} minWidth={600}>
        <Modal.Header onClose={successModalControl.modalProps.onClose}>
          {t('All Devices Assigned an E911 Address')}
        </Modal.Header>
        <Modal.Body>
          <Text css={{ marginBottom: 'var(--fabric-space-gap-md)' }}>
            {t(
              'All active devices have been successfully assigned an E911 address. All of your devices are ready for emergency calling.'
            )}
          </Text>
          <Text>
            {t('To verify your E911 configuration, dial 933 from a device. This will not call emergency services.')}
          </Text>
        </Modal.Body>
        <Modal.Footer
          secondary={{
            label: t('Close'),
            onClick: successModalControl.modalProps.onClose,
          }}
        />
      </Modal>
    </>
  );
};

type AssignMultipleDevicesModalProps = ModalControlModalProps & {
  addressId: string;
  addressName: string;
  fullAddress: string;
  devices: AssignableDevice[];
  tenantId: string;
  isMultiLocation: boolean;
  selectedDeviceIds: string[];
  onSelectedDevicesChange: (deviceIds: string[]) => void;
  onAssign: () => Promise<void>;
  isLoading: boolean;
};

const AssignMultipleDevicesModal = ({
  addressId,
  addressName,
  fullAddress,
  devices,
  tenantId,
  isMultiLocation,
  selectedDeviceIds,
  onSelectedDevicesChange,
  onAssign,
  isLoading,
  ...modalProps
}: AssignMultipleDevicesModalProps) => {
  const { t } = useTranslation('phone', { keyPrefix: 'e911' });
  const [selectionSyncKey, setSelectionSyncKey] = useState(0);
  const confirmFieldProps = useFormField({ type: 'checkbox', value: false }, [addressId]);
  const isAddingNewAddress = addressId === 'addNewAddress';

  const deviceTableData = devices.map((device) => ({
    id: device.deviceId,
    deviceName: device.name || '',
    macAddress: device.macAddress || '',
    location: device.location?.name || '',
  }));

  const allDeviceIds = deviceTableData.map((device) => device.id);
  const areAllDevicesSelected =
    allDeviceIds.length > 0 && allDeviceIds.every((deviceId) => selectedDeviceIds.includes(deviceId));

  return (
    <Modal {...modalProps} minWidth={600} maxWidth={600}>
      <Modal.Header>{t('Assign Address to Devices')}</Modal.Header>
      <Modal.Label>{fullAddress}</Modal.Label>
      <Modal.Body>
        <Text css={{ marginBottom: 'var(--fabric-space-gap-md)' }}>
          {t('Select the devices that should use this E911 address, {{addressName}} ({{fullAddress}}).', {
            addressName,
            fullAddress,
          })}
        </Text>

        <Table
          key={selectionSyncKey}
          data={deviceTableData}
          uniqueRowId={(row) => row.id}
          isSelectable
          rowSelectionConfig={{
            hideBulkSelection: true,
            onSelectionToggle: (_selectedRows, selectedRowsData) => {
              const selectedIds = selectedRowsData.map((row) => row.id);
              onSelectedDevicesChange(selectedIds);
            },
            initialState: selectedDeviceIds.reduce<Record<string, boolean>>((acc, deviceId) => {
              acc[deviceId] = true;
              return acc;
            }, {}),
          }}
          colConfig={[
            {
              Header: t('Device Name'),
              accessor: 'deviceName',
              id: 'device-name',
            },
            {
              Header: t('MAC Address'),
              accessor: 'macAddress',
              id: 'mac-address',
            },
            {
              accessor: ({ location }) => location,
              omit: !isMultiLocation,
              id: 'location',
              Header: t('Location'),
            },
          ]}
          emptyStateConfig={{
            type: 'sync_your_phone',
            header: t('No Devices'),
            description: t('No devices available to assign.'),
          }}
        />
        <CheckboxField
          {...confirmFieldProps}
          name='e911-confirm'
          label={t('I confirm that the selected devices are located at {{fullAddress}}.', { fullAddress })}
          disabled={!addressId || isAddingNewAddress}
        />
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Assign to Selected Devices'),
          onClick: onAssign,
          disabled: selectedDeviceIds.length === 0 || isLoading || isAddingNewAddress || !confirmFieldProps.value,
          loading: isLoading,
          trackingId: trackingId({
            context: 'setting',
            feature: 'e911',
            details: 'assign-e911-address-selected-devices',
          }),
        }}
        secondary={{
          label: areAllDevicesSelected ? t('Deselect All') : t('Select All'),
          onClick: () => {
            onSelectedDevicesChange(areAllDevicesSelected ? [] : allDeviceIds);
            setSelectionSyncKey((prev) => prev + 1);
          },
        }}
        tertiary={{
          label: t('Back'),
          onClick: modalProps.onClose,
        }}
      />
    </Modal>
  );
};
